import { useCallback, useMemo } from "react";
import { Link } from "gatsby";
import { PropTypes } from "prop-types";

import { useAddToCartWithLuxe } from "@context/LuxeProvider";
import { useTracking } from "@context/Tracking";

import { useLocalizedSentenceDict } from "@hooks/useSentenceDict";
import useDiscountsInfo from "@hooks/useDiscountsInfo";
import useDeviceDetect from "@hooks/useDeviceDetect";
import { useCurrentCountryCode, useCurrentCurrencyCode } from "@hooks/usePrices";
import useBundleProductsWithPrices from "@hooks/useBundleProductsWithPrices";

import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { resizeShopifyImage, prepareAddToCart } from "@helpers/product";
import { useApplyIgPricesOnProducts, useApplyIgPricesOnVariants } from "@helpers/intelligems";

import { ComponentType } from "@constants/ComponentType";
import TAG from "@constants/Tag";

import PriceContainer from "@components/PriceContainer";
import QuickAddToCart from "@components/QuickAddToCart";

function UpSellItem({ product, atcButtonDisabled = false, pricesLoading, tierDiscountPercent }) {
  const { trackRecommendedProduct } = useTracking();
  const addToCartWithLuxe = useAddToCartWithLuxe();
  const discountsInfo = useDiscountsInfo();
  const { isMobile } = useDeviceDetect();

  const countryCode = useCurrentCountryCode();
  const currencyCode = useCurrentCurrencyCode();

  const dict = useLocalizedSentenceDict();

  const { products: bundleProductsWithPrices } = useBundleProductsWithPrices(
    product?.contentful?.bundleProducts,
    countryCode,
    !!product
  );

  const fullProductCopy = useMemo(
    () => ({ ...product, bundleProducts: bundleProductsWithPrices }),
    [product, bundleProductsWithPrices]
  );

  const igPricesForProduct = useApplyIgPricesOnVariants(product.id, product.variants);
  const igPricesForBundle = useApplyIgPricesOnProducts(
    fullProductCopy.bundleProducts?.map((p) => ({
      productId: p?.node?.id,
      variant: p?.node?.variants?.[0],
      prices: p,
    })) ?? []
  );

  const isFinalSale =
    (product?.tags || []).includes(TAG.FINAL_SALE) ||
    (product?.tags || []).includes(TAG.CUSTOMIZABLE);

  const handleAddToCart = useCallback(
    async ({ selectedVariant }) => {
      const variant = selectedVariant || fullProductCopy.variants[0];
      const { productsToAdd, dataProductsToAdd } = prepareAddToCart({
        product: fullProductCopy,
        selectedVariant: variant,
        productPrices: igPricesForProduct[variant.id],
        bundlePrices: igPricesForBundle,
        productOverrides: { isFinalSale },
        countryCode,
        currencyCode,
        discountsInfo,
      });

      productsToAdd.forEach((p) => {
        trackRecommendedProduct("add", "Side Cart", {
          productId: p?.productId ?? p?.id,
          productTitle: p?.title,
          productCategory: p?.category ?? p?.productType,
        });
      });
      if (dataProductsToAdd.length > 0) {
        await addToCartWithLuxe({ dataProductsToAdd });
      }
    },
    [
      addToCartWithLuxe,
      countryCode,
      currencyCode,
      discountsInfo,
      fullProductCopy,
      isFinalSale,
      trackRecommendedProduct,
      igPricesForProduct,
      igPricesForBundle,
    ]
  );

  return (
    <div
      className="flex flex-row justify-start relative border border-black rounded-[8px] mr-[10px] p-[10px]"
      key={product.handle}
    >
      <Link
        className="w-[100px] h-[125px] mobile:w-[80px] mobile:h-[100px] object-cover overflow-hidden"
        to={`/products/${product.handle}?origin_content=recommended-upsell_sidecart`}
      >
        <img
          src={resizeShopifyImage(product.images[0].url, "x200")}
          alt={product.images[0].altText}
        />
      </Link>
      <div className="flex flex-col justify-between items-end w-[calc(100%-110px)] mobile:w-[calc(100%-90px)] ml-[10px]">
        <div className="flex justify-between w-full">
          <p className="typo-body font-bold">{product.title.split(" - ")[1] || product.title}</p>
          <PriceContainer
            productId={product.id}
            tags={product.tags}
            selectedVariant={product.variants[0]}
            componentType={ComponentType.SIDE_CART_UPSELL}
            prices={product}
            pricesLoading={pricesLoading}
            tierDiscountPercent={tierDiscountPercent}
          />
        </div>

        {isFinalSale && <p className="typo-body">{dict.get("Final Sale")}</p>}

        <QuickAddToCart
          onQuickAddToCart={handleAddToCart}
          product={product}
          isMobile={isMobile}
          componentType={ComponentType.SIDE_CART_UPSELL}
          atcButtonDisabled={atcButtonDisabled}
        />
      </div>
    </div>
  );
}

UpSellItem.propTypes = {
  product: PropTypes.shape({
    handle: PropTypes.string,
    title: PropTypes.string,
    recommendationId: PropTypes.string,
    currencyCode: PropTypes.string,
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      })
    ),
    images: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        altText: PropTypes.string,
      })
    ),
    tags: PropTypes.arrayOf(PropTypes.string),
    productType: PropTypes.string,
    category: PropTypes.string,
    id: PropTypes.string,
    contentful: PropTypes.shape({
      bundleProducts: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          title: PropTypes.string,
        })
      ),
    }),
  }).isRequired,
  pricesLoading: PropTypes.bool,
  atcButtonDisabled: PropTypes.bool,
  tierDiscountPercent: PropTypes.number,
};

export default withALErrorBoundary({
  name: "UpSellItem",
  priority: "P2",
})(UpSellItem);
